import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';

import styles from '../Home/CreateExam/Create.module.scss';
import { getSubject } from '../../redux/apiRequest';
import AssignExamModal from './AssignExamModal';

const cx = classNames.bind(styles);

function CreateExamModal({ onClose, user, axiosJWT }) {
    const dispatch = useDispatch();
    const [selectedSubjects, setSelectedSubjects] = useState([{ subjectId: '', numQuestions: 0 }]);
    const [formErrors, setFormErrors] = useState({
        selectedSubjects: '',
    });

    const [openAssignModal, setOpenAssignModal] = useState(false);

    const subjectsState = useSelector((state) => state.subjects?.subjects);
    const subjects = useMemo(() => subjectsState?.listsubjects || [], [subjectsState]);

    useEffect(() => {
        if (user?.accessToken) {
            getSubject(user.accessToken, dispatch, axiosJWT);
        }
    }, [user?.accessToken, dispatch, axiosJWT]);

    const totalQuestions = selectedSubjects.reduce(
        (total, subject) => total + (Number(subject.numQuestions) || 0),
        0,
    );

    const validateForm = () => {
        const errors = {
            selectedSubjects: ''
        };

        let hasErrors = false;

        const hasValidSubject = selectedSubjects.some(
            (subject) => subject.subjectId && Number(subject.numQuestions) > 0,
        );

        if (!hasValidSubject) {
            errors.selectedSubjects = 'Vui lòng chọn ít nhất một chuyên đề và số câu hỏi hợp lệ.';
            hasErrors = true;
        }

        setFormErrors(errors);
        return !hasErrors;
    };

    const handleNext = () => {
        if (!validateForm()) return;
        setOpenAssignModal(true);
    };

    const handleSubjectChange = (index, field, value) => {
        const newSelectedSubjects = [...selectedSubjects];
        const current = newSelectedSubjects[index] || { subjectId: '', numQuestions: 0 };

        let nextValue = value;
        if (field === 'numQuestions') {
            const parsedValue = Number(value);
            nextValue = Number.isNaN(parsedValue) ? '' : Math.max(0, parsedValue);
        }

        newSelectedSubjects[index] = {
            ...current,
            [field]: nextValue,
        };

        setSelectedSubjects(newSelectedSubjects);
    };

    const addSubject = () => {
        setSelectedSubjects((prev) => [...prev, { subjectId: '', numQuestions: 0 }]);
    };

    const removeSubject = (index) => {
        setSelectedSubjects((prev) => {
            if (prev.length === 1) {
                return [{ subjectId: '', numQuestions: 0 }];
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const getAvailableSubjects = (index) => {
        const selectedIds = selectedSubjects
            .map((subject, idx) => (idx === index ? null : subject.subjectId))
            .filter(Boolean)
            .map((id) => String(id));

        return subjects.filter((subject) => !selectedIds.includes(String(subject.id)));
    };

    const handleClose = () => {
        if (typeof onClose === 'function') {
            onClose(false);
        }
    };

    const handleExamCreated = () => {
        setSelectedSubjects([{ subjectId: '', numQuestions: 0 }]);
        if (typeof onClose === 'function') {
            onClose(false);
        }
    };
    return (
        <>
            <div className={cx('overlay')}>
                <div className={cx('modal')}>
                    <button type="button" className={cx('close-button')} onClick={handleClose} aria-label="Đóng">
                        ×
                    </button>

                    <header className={cx('header')}>
                        <span className={cx('header__subtitle')}>Tạo đề thi online</span>
                        <h2 className={cx('header__title')}>Cá nhân hoá đề thi từ các chuyên đề</h2>
                    </header>

                    <section className={cx('metrics')}>
                        <div className={cx('metric-card')}>
                            <span className={cx('metric-card__label')}>Chuyên đề đã chọn</span>
                            <span className={cx('metric-card__value')}>{selectedSubjects.length}</span>
                        </div>
                        <div className={cx('metric-card')}>
                            <span className={cx('metric-card__label')}>Tổng số câu hỏi</span>
                            <span className={cx('metric-card__value')}>{totalQuestions}</span>
                        </div>
                    </section>

                    <section className={cx('subject-section')}>
                        <div className={cx('subject-section__header')}>
                            <h3>Thiết lập chuyên đề</h3>
                            <button type="button" className={cx('add-subject')} onClick={addSubject}>
                                + Thêm chuyên đề
                            </button>
                        </div>

                        {formErrors.selectedSubjects && (
                            <div className={cx('error-message')}>{formErrors.selectedSubjects}</div>
                        )}

                        <div className={cx('subject-list')}>
                            {selectedSubjects.map((subject, index) => (
                                <div key={index} className={cx('subject-row')}>
                                    <div className={cx('field')}>
                                        <label htmlFor={`subject-${index}`}>Chuyên đề</label>
                                        <select
                                            id={`subject-${index}`}
                                            value={subject.subjectId}
                                            onChange={(e) => handleSubjectChange(index, 'subjectId', e.target.value)}
                                        >
                                            <option value="">Chọn chuyên đề</option>
                                            {getAvailableSubjects(index).map((sub) => (
                                                <option key={sub.id} value={sub.id}>
                                                    {sub.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={cx('field')}>
                                        <label htmlFor={`question-${index}`}>Số câu hỏi</label>
                                        <input
                                            id={`question-${index}`}
                                            type="number"
                                            min="0"
                                            value={subject.numQuestions}
                                            onChange={(e) => handleSubjectChange(index, 'numQuestions', e.target.value)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={cx('remove-button')}
                                        onClick={() => removeSubject(index)}
                                    >
                                        Xoá
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <footer className={cx('footer')}>
                        <div className={cx('actions')}>
                            <button type="button" className={cx('secondary-button')} onClick={handleClose}>
                                Huỷ
                            </button>
                            <button type="button" className={cx('primary-button')} onClick={handleNext}>
                                Tạo đề thi
                            </button>
                        </div>
                    </footer>
                </div>
            </div>

            {openAssignModal && (
                <AssignExamModal
                    onClose={() => setOpenAssignModal(false)}
                    user={user}
                    axiosJWT={axiosJWT}
                    selectedSubjects={selectedSubjects}
                    onExamCreated={handleExamCreated}
                />
            )}
        </>
    );
}

export default CreateExamModal;
